import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Map from '../components/Map'
import citiesData from '../../src/data/cities.json'
import { useSearchFilters } from '../components/SearchFiltersContext'

interface SearchFilters {
  imLookingFor: string[]
  orientation: string[]
  iLiveIn: string[]
  distance: string[]
}

function RegionsPage() {
  const { t } = useTranslation()
  const { setFilters } = useSearchFilters()
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([])

  const provinces = useMemo(
    () => citiesData.map(province => province.province),
    [],
  )

  const handleProvinceSelect = (province: string) => {
    setSelectedProvince(province)
    const provinceId = province.toLowerCase().replace(/\s+/g, '-')
    setSelectedProvinces([provinceId])
    setSearchTerm('')
  }

  const handleCitySelect = (city: string) => {
    const currentFilters = JSON.parse(
      localStorage.getItem('searchFilters') ||
        JSON.stringify({
          imLookingFor: [],
          orientation: [],
          iLiveIn: [],
          distance: [],
        }),
    )

    const newFilters: SearchFilters = {
      ...currentFilters,
      iLiveIn: [city],
    }

    localStorage.setItem('searchFilters', JSON.stringify(newFilters))
    setFilters(newFilters)
    window.location.href = '/'
  }

  const handleProvinceClick = (provinceId: string) => {
    const province = provinces.find(
      p => p.toLowerCase().replace(/\s+/g, '-') === provinceId,
    )
    if (province) {
      setSelectedProvince(province)
      setSelectedProvinces([provinceId])
      setSearchTerm('')
    }
  }

  const filteredCities = useMemo(() => {
    if (!selectedProvince) return []

    const provinceData = citiesData.find(p => p.province === selectedProvince)
    if (!provinceData) return []

    return provinceData.cities
      .filter(
        city =>
          city.city_fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
          city.postal_code.includes(searchTerm),
      )
      .sort((a, b) => a.city_fr.localeCompare(b.city_fr))
  }, [selectedProvince, searchTerm])

  return (
    <>
      <div
        id='premium-slider'
        className='text-center w-full align-middle bg-redpink text-white  py-3'
      >
        <p className='uppercase text-2xl'>{t('regions-corner')}</p>
        <p className=''>{t('regions-corner-desc')}</p>
      </div>
      <div className='max-w-7xl lg:mx-auto my-5 mx-2'>
        <div
          className={`lg:grid ${
            selectedProvince ? 'grid-cols-5 gap-8' : 'grid-cols-1'
          }`}
        >
          <div className={`${selectedProvince ? 'col-span-2' : ''}`}>
            <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 py-2 bg-white'>
              {provinces.map(province => (
                <button
                  key={province}
                  onClick={() => handleProvinceSelect(province)}
                  className={`p-2 rounded-lg transition-colors ${
                    selectedProvince === province
                      ? 'bg-redpink text-white'
                      : 'bg-anthracite hover:bg-redpink text-white text-sm'
                  }`}
                >
                  {t(`provinces.${province}`)}
                </button>
              ))}
            </div>
            <div className='w-full bg-grisclair rounded-lg overflow-hidden'>
              <div
                className={
                  selectedProvince
                    ? 'scale-100 origin-top w-full'
                    : 'scale-80 origin-top w-full'
                }
              >
                <Map
                  selectedProvinces={selectedProvinces}
                  setSelectedProvinces={setSelectedProvinces}
                  onProvinceClick={handleProvinceClick}
                  singleSelect={true}
                  showLegend={false}
                />
              </div>
            </div>
          </div>

          {selectedProvince && (
            <div className='col-span-3'>
              <div className='mb-4'>
                <input
                  type='text'
                  placeholder={t('search-cities')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='w-full p-2 border border-anthracite2 rounded focus:ring-1 focus:ring-redpink outline-none'
                />
              </div>
              <div className='grid grid-cols-3 gap-2'>
                {filteredCities.map((city, index) => (
                  <button
                    key={index}
                    onClick={() => handleCitySelect(city.city_fr)}
                    className='text-left text-sm py-2 px-3 hover:bg-redpink hover:text-white bg-grisclair hover:pl-4 rounded transition-all'
                  >
                    {city.city_fr} ({city.postal_code})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default RegionsPage
